import { supabase } from '@/utils/supabase';

const BUCKET_NAME = 'company-logos';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

export interface CompanyBranding {
  companyName: string;
  companyLogoUrl: string | null;
  isProUser: boolean;
  primaryColor?: string; // Couleur dominante Pro (ex: '#B91C1C'). undefined = bleu DPEWizard
}

export const companyBrandingService = {
  async uploadCompanyLogo(file: File, userId: string): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      console.log('[CompanyBranding] Starting logo upload for user:', userId);

      if (file.size > MAX_FILE_SIZE) {
        return {
          success: false,
          error: 'Le fichier est trop volumineux. Taille maximale : 2 MB.'
        };
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        return {
          success: false,
          error: 'Format de fichier non autorisé. Utilisez PNG, JPG, JPEG ou SVG.'
        };
      }

      const existingLogo = await this.getCompanyLogoUrl(userId);
      if (existingLogo) {
        console.log('[CompanyBranding] Deleting existing logo:', existingLogo);
        await this.deleteCompanyLogo(userId);
      }

      const fileExtension = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExtension}`;
      console.log('[CompanyBranding] Uploading to:', fileName);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('[CompanyBranding] Upload error:', uploadError);
        return {
          success: false,
          error: 'Erreur lors de l\'upload du logo. Veuillez réessayer.'
        };
      }

      console.log('[CompanyBranding] Upload successful:', uploadData);

      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(fileName);

      const publicUrl = urlData.publicUrl;
      console.log('[CompanyBranding] Public URL:', publicUrl);
      console.log('[CompanyBranding] Updating profile with logo URL...');

      const { data: updateData, error: updateError } = await supabase
        .from('profiles')
        .update({ company_logo_url: publicUrl })
        .eq('id', userId)
        .select('id, company_logo_url');

      if (updateError) {
        console.error('[CompanyBranding] Profile update error:', updateError);
        console.error('[CompanyBranding] Error details:', JSON.stringify(updateError, null, 2));
        await supabase.storage.from(BUCKET_NAME).remove([fileName]);
        return {
          success: false,
          error: `Erreur lors de la mise à jour du profil: ${updateError.message || 'Erreur inconnue'}`
        };
      }

      console.log('[CompanyBranding] Profile updated successfully:', updateData);

      if (!updateData || updateData.length === 0) {
        console.warn('[CompanyBranding] WARNING: Update returned no data. Profile may not have been updated.');
      }

      return {
        success: true,
        url: publicUrl
      };
    } catch (error) {
      console.error('[CompanyBranding] Upload company logo error:', error);
      return {
        success: false,
        error: 'Une erreur inattendue est survenue.'
      };
    }
  },

  async deleteCompanyLogo(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_logo_url')
        .eq('id', userId)
        .single();

      if (!profile?.company_logo_url) {
        return { success: true };
      }

      const urlParts = profile.company_logo_url.split('/');
      const fileName = `${userId}/${urlParts[urlParts.length - 1]}`;

      const { error: deleteError } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([fileName]);

      if (deleteError) {
        console.error('Delete storage error:', deleteError);
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ company_logo_url: null })
        .eq('id', userId);

      if (updateError) {
        console.error('Profile update error:', updateError);
        return {
          success: false,
          error: 'Erreur lors de la mise à jour du profil.'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Delete company logo error:', error);
      return {
        success: false,
        error: 'Une erreur inattendue est survenue.'
      };
    }
  },

  async getCompanyLogoUrl(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('company_logo_url')
        .eq('id', userId)
        .single();

      if (error || !data) {
        return null;
      }

      return data.company_logo_url;
    } catch (error) {
      console.error('Get company logo error:', error);
      return null;
    }
  },

  async updateCompanyName(companyName: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[CompanyBranding] Updating company name for user:', userId);
      console.log('[CompanyBranding] New company name:', companyName);

      if (!companyName || companyName.trim().length === 0) {
        return {
          success: false,
          error: 'Le nom d\'entreprise ne peut pas être vide.'
        };
      }

      if (companyName.length > 100) {
        return {
          success: false,
          error: 'Le nom d\'entreprise ne peut pas dépasser 100 caractères.'
        };
      }

      const { data: updateData, error } = await supabase
        .from('profiles')
        .update({ company_name: companyName.trim() })
        .eq('id', userId)
        .select('id, company_name');

      if (error) {
        console.error('[CompanyBranding] Update company name error:', error);
        console.error('[CompanyBranding] Error details:', JSON.stringify(error, null, 2));
        return {
          success: false,
          error: `Erreur lors de la mise à jour du nom d'entreprise: ${error.message || 'Erreur inconnue'}`
        };
      }

      console.log('[CompanyBranding] Company name updated successfully:', updateData);

      if (!updateData || updateData.length === 0) {
        console.warn('[CompanyBranding] WARNING: Update returned no data. Profile may not have been updated.');
      }

      return { success: true };
    } catch (error) {
      console.error('[CompanyBranding] Update company name error:', error);
      return {
        success: false,
        error: 'Une erreur inattendue est survenue.'
      };
    }
  },

  async updatePrimaryColor(color: string | null, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (color !== null) {
        if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
          return { success: false, error: 'Format de couleur invalide. Utilisez un code hexadécimal (ex : #B91C1C).' };
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({ primary_color: color })
        .eq('id', userId);

      if (error) {
        console.error('[CompanyBranding] Update primary color error:', error);
        return { success: false, error: `Erreur lors de la mise à jour de la couleur : ${error.message}` };
      }

      return { success: true };
    } catch (error) {
      console.error('[CompanyBranding] Update primary color error:', error);
      return { success: false, error: 'Une erreur inattendue est survenue.' };
    }
  },

  async getCompanyBranding(userId: string): Promise<CompanyBranding> {
    try {
      console.log('[CompanyBranding] Fetching branding for user:', userId);

      const { data, error } = await supabase
        .from('profiles')
        .select('company_name, company_logo_url, subscription_price_id, primary_color')
        .eq('id', userId)
        .single();

      if (error || !data) {
        console.error('[CompanyBranding] Error fetching branding:', error);
        return {
          companyName: 'dpewizard',
          companyLogoUrl: null,
          isProUser: false
        };
      }

      console.log('[CompanyBranding] Fetched data:', {
        company_name: data.company_name,
        company_logo_url: data.company_logo_url,
        subscription_price_id: data.subscription_price_id,
        primary_color: data.primary_color
      });

      const { isProSubscription } = await import('@/stripe-config');
      const isPro = isProSubscription(data.subscription_price_id);

      const branding: CompanyBranding = {
        companyName: data.company_name || 'dpewizard',
        companyLogoUrl: data.company_logo_url,
        isProUser: isPro,
        primaryColor: isPro ? (data.primary_color ?? undefined) : undefined
      };

      console.log('[CompanyBranding] Returning branding:', branding);
      return branding;
    } catch (error) {
      console.error('[CompanyBranding] Get company branding error:', error);
      return {
        companyName: 'dpewizard',
        companyLogoUrl: null,
        isProUser: false
      };
    }
  }
};
